import React, { useState, useCallback } from 'react';
import { styled } from '@mui/system';
import ListItem from '@mui/material/ListItem/index';
import ListItemIcon from '@mui/material/ListItemIcon/index';
import ListItemText from '@mui/material/ListItemText/index';
import Typography from '@mui/material/Typography';
import { Accordion, AccordionSummary, AccordionDetails, IconButton } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import eventIcon from '#component/icon/event';
import { WarningLabel } from './warning-label';
import { getGroupDate, getEventDate } from './helper';
import { AlertConfirmationDialog, ListConfirmationButton } from './alert-confirmation';

const HoverableListItem = styled(ListItem)(({ theme }) => ({
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.grey['200']
  }
}));

const IconCtn = styled('div')(() => ({
  position: 'relative',
  minWidth: 37
}));

const EventsIcon = styled(ListItemIcon)(() => ({
  position: 'relative',
  minWidth: 32,
  zIndex: 1,
  '& svg': {
    background: '#fff',
    borderRadius: '50%'
  }
}));

const EventsIcon2 = styled(ListItemIcon)(() => ({
  position: 'absolute',
  left: 5,
  zIndex: 0
}));

const EventsLabel = styled(Typography)(() => ({
  maxWidth: '50%',
  whiteSpace: 'nowrap',
  flexGrow: 1
}));

const StyledListItemText = styled(ListItemText)(() => ({
  '& .time': {
    fontSize: 12
  },
  '& .events-label-info': {
    display: 'flex',
    flexWrap: 'wrap'
  }
}));

const StyledAccordion = styled(Accordion)(() => ({
  boxShadow: 'none',
  '&:before': {
    display: 'none'
  },
  '&.Mui-expanded': {
    margin: 0
  }
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  padding: `0 ${theme.spacing(1)} 0 0`,
  [theme.breakpoints.down('md')]: {
    padding: `0 ${theme.spacing(2)} 0 0`
  },
  '&:hover': {
    backgroundColor: theme.palette.grey['200']
  },
  '& li': {
    '&:hover': {
      backgroundColor: 'transparent'
    }
  },
  '& .MuiAccordionSummary-content': {
    margin: 0
  },
  '&.Mui-expanded': {
    minHeight: 'unset',
    '& .MuiAccordionSummary-content': {
      margin: 0
    }
  },
  '& .MuiAccordionSummary-expandIcon': {
    padding: '0px 12px'
  }
}));

const StyledListItem = styled(ListItem)(() => ({
  paddingRight: 0,
  '& div:first-of-type': {
    minWidth: 37
  }
}));

const StyledAccordionDetails = styled(AccordionDetails)(() => ({
  flexWrap: 'wrap'
}));

const StyledListItemIcon = styled(ListItemIcon)(() => ({
  minWidth: 32
}));

export const EventListItem = props => {
  const {
    eventsGroup,
    account,
    isMobile,
    handleClick,
    handleHover,
    handleEventHover,
    handleGroupHover,
    handleGroupClosing,
    hoverEventId,
    setHoverEventId,
    syncSelectedDevicesWithStore
  } = props;

  const [opened, setOpened] = useState(false);
  const [confirmationPopupData, setConfirmationPopupData] = useState(null);
  const alertIdentifiers = ['shipment_40075', 'appliance_50055'];
  const alertConfirmAccess = account?.userType < 10 && account?.allowManageAlertAcceptance === 1;

  const handleEventClick = useCallback(eventItem => e => {
    if (!isMobile) {
      e.stopPropagation();
    }
    handleClick(e, eventItem);
  }, [handleClick, isMobile]);

  const handleEventChange = useCallback(eventItem => (e, expanded) => {
    setOpened(!opened);
    if (!expanded) {
      handleGroupClosing(e, eventItem);
    }
  }, [handleGroupClosing, opened]);

  const eventItem = Array.isArray(eventsGroup) && eventsGroup.length === 1 ?
    eventsGroup[0] : eventsGroup;

  const handleItemHover = (e, item) => {
    handleHover(e, item);

    if (e.type === 'mouseleave') {
      setHoverEventId(null);
    } else {
      setHoverEventId(item.details?.logId ?? null);
    }
  };

  const handleOpenConfirmationPopup = (e, item) => {
    e.stopPropagation();
    setConfirmationPopupData(item);
  };

  if (Array.isArray(eventItem)) {
    const startDate = eventItem[eventItem.length - 1];
    const warnings = eventItem.filter(evnt =>
      ['dw', 'ex', 'shipment_40075', 'appliance_50055'].includes(evnt.identifier));
    const events = `${eventItem.length} ${eventItem.length === 1 ? 'event' : 'events'}`;

    return (
      <>
        <StyledAccordion
          TransitionProps={{
            timeout: { appear: 0, enter: 500, exit: 150 }
          }}
          onChange={handleEventChange(eventItem)}
          square
        >
          <StyledAccordionSummary
            expandIcon={
              <IconButton>
                <ExpandMoreIcon />
              </IconButton>
              }
            onMouseEnter={handleGroupHover(eventItem)}
            onMouseLeave={handleGroupHover(eventItem)}
          >
            <StyledListItem
              onClick={handleEventClick(eventItem)}
            >
              <IconCtn>
                <EventsIcon>
                  {eventIcon(eventItem)}
                </EventsIcon>
                <EventsIcon2>
                  {eventIcon(eventItem)}
                </EventsIcon2>
              </IconCtn>
              <StyledListItemText>
                <Typography color="textSecondary" className="time">
                  {getGroupDate(startDate, account)}
                </Typography>
                <span className="events-label-info">
                  {warnings.length > 0 && <WarningLabel warnings={warnings} />}
                  <EventsLabel>
                    {events}
                  </EventsLabel>
                </span>
              </StyledListItemText>
            </StyledListItem>
          </StyledAccordionSummary>
          <StyledAccordionDetails>
            {opened && eventItem.map((item, i) => (
              <React.Fragment key={i}>
                {handleEventHover ? (
                  <HoverableListItem
                    onClick={handleEventClick(item)}
                    onMouseEnter={e => handleItemHover(e, item)}
                    onMouseLeave={e => handleItemHover(e, item)}
                  >
                    <StyledListItemIcon>{eventIcon(item)}</StyledListItemIcon>
                    <StyledListItemText>
                      {getEventDate(item, account)}
                    </StyledListItemText>
                    {(item.details?.logId === hoverEventId || isMobile) && alertConfirmAccess &&
                      alertIdentifiers.includes(item.identifier) &&
                        <ListConfirmationButton
                          handleOpenConfirmationPopup={handleOpenConfirmationPopup}
                          item={item}
                          isMobile={isMobile}
                        />}
                  </HoverableListItem>
                ) : (
                  <ListItem
                    onClick={handleEventClick(item)}
                    onMouseEnter={e => handleItemHover(e, item)}
                    onMouseLeave={e => handleItemHover(e, item)}
                  >
                    <StyledListItemIcon>{eventIcon(item)}</StyledListItemIcon>
                    <StyledListItemText>
                      {getEventDate(item, account)}
                    </StyledListItemText>
                    {(item.details?.logId === hoverEventId || isMobile) && alertConfirmAccess &&
                      alertIdentifiers.includes(item.identifier) &&
                        <ListConfirmationButton
                          handleOpenConfirmationPopup={handleOpenConfirmationPopup}
                          item={item}
                          isMobile={isMobile}
                        />}
                  </ListItem>
                )}
              </React.Fragment>
            ))}
          </StyledAccordionDetails>
        </StyledAccordion>
        {confirmationPopupData &&
          <AlertConfirmationDialog
            isOpen={Boolean(confirmationPopupData)}
            setConfirmationPopupData={setConfirmationPopupData}
            item={confirmationPopupData}
            syncSelectedDevicesWithStore={syncSelectedDevicesWithStore}
          />}
      </>
    );
  }

  return (
    <>
      {handleEventHover ? (
        <HoverableListItem
          onClick={handleEventClick(eventItem)}
          onMouseEnter={e => handleItemHover(e, eventItem)}
          onMouseLeave={e => handleItemHover(e, eventItem)}
        >
          <StyledListItemIcon>{eventIcon(eventItem)}</StyledListItemIcon>
          <StyledListItemText>
            {getEventDate(eventItem, account)}
          </StyledListItemText>
          {(eventItem.details?.logId === hoverEventId || isMobile) &&
            alertIdentifiers.includes(eventItem.identifier) && alertConfirmAccess &&
            <ListConfirmationButton
              handleOpenConfirmationPopup={handleOpenConfirmationPopup}
              item={eventItem}
              isMobile={isMobile}
            />}
        </HoverableListItem>
      ) : (
        <ListItem
          onClick={handleEventClick(eventItem)}
          onMouseEnter={e => handleItemHover(e, eventItem)}
          onMouseLeave={e => handleItemHover(e, eventItem)}
        >
          <StyledListItemIcon>{eventIcon(eventItem)}</StyledListItemIcon>
          <StyledListItemText>
            {getEventDate(eventItem, account)}
          </StyledListItemText>
          {(eventItem.details?.logId === hoverEventId || isMobile) &&
            alertIdentifiers.includes(eventItem.identifier) && alertConfirmAccess &&
            <ListConfirmationButton
              handleOpenConfirmationPopup={handleOpenConfirmationPopup}
              item={eventItem}
              isMobile={isMobile}
            />}
        </ListItem>
      )}
      {confirmationPopupData &&
        <AlertConfirmationDialog
          isOpen={Boolean(confirmationPopupData)}
          setConfirmationPopupData={setConfirmationPopupData}
          item={confirmationPopupData}
          syncSelectedDevicesWithStore={syncSelectedDevicesWithStore}
        />}
    </>
  );
};
